/**
 * Immersive Project Showcase
 * 
 * Next-generation project showcase with:
 * - Parallax scrolling effects
 * - Video background support
 * - Interactive hotspots
 * - Scroll-triggered animations
 * - Cinematic transitions
 * - 3D depth effects
 */

'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from 'framer-motion';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import type { MediaLink } from '@/lib/api/clients/properties';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface ImmersiveProjectShowcaseProps {
  project: PropertyDevelopmentProject;
  media?: MediaLink[];
  brandLogo?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  videoUrl?: string;
  messages: {
    overview: string;
    features: string;
    location: string;
    playVideo: string;
    pauseVideo: string;
    exploreProject: string;
    scrollToDiscover: string;
  };
  locale?: string;
  className?: string;
}

interface Hotspot {
  id: string;
  x: number; // percentage from left
  y: number; // percentage from top
  title: string;
  description: string;
}

/**
 * Immersive Project Showcase Component
 */
export function ImmersiveProjectShowcase({
  project,
  media = [],
  brandLogo,
  brandColors,
  videoUrl,
  messages,
  locale: _locale = 'en',
  className = '',
}: ImmersiveProjectShowcaseProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const isHeroInView = useInView(heroRef, { amount: 0.5 });
  
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  // Smooth spring animations
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [0, 300]), springConfig);
  const opacity = useSpring(useTransform(scrollYProgress, [0, 0.5], [1, 0]), springConfig);
  const scale = useSpring(useTransform(scrollYProgress, [0, 1], [1, 1.2]), springConfig);

  const colors = {
    primary: brandColors?.primary || '#243462',
    secondary: brandColors?.secondary || '#040813',
    accent: brandColors?.accent || '#BBA339',
  };

  // Sample hotspots for interactive exploration
  const hotspots: Hotspot[] = [
    {
      id: 'entrance',
      x: 30,
      y: 60,
      title: 'Grand Entrance',
      description: 'Impressive lobby with 24/7 concierge',
    },
    {
      id: 'amenities',
      x: 70,
      y: 40,
      title: 'Amenities Deck',
      description: 'Pool, gym, and entertainment spaces',
    },
  ];

  const heroImage = media.find((m) => m.usageContext === 'PRIMARY_HERO') || media[0];

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        analyticsEvents.videoStart(project.id, project.name);
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Hero Section with Parallax */}
      <div
        ref={heroRef}
        className="relative h-screen min-h-[800px] overflow-hidden"
      >
        {/* Parallax Background */}
        <motion.div
          className="absolute inset-0"
          style={{ y, scale }}
        >
          {videoUrl ? (
            <>
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                loop
                muted
                playsInline
                poster={heroImage?.media?.url}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>

              {/* Video Controls */}
              <button
                onClick={toggleVideo}
                className="absolute bottom-8 right-8 z-20 flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-gray-900 shadow-2xl backdrop-blur-md transition-all hover:bg-white hover:scale-110"
              >
                {isVideoPlaying ? (
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            </>
          ) : (
            heroImage?.media?.url && (
              <Image
                src={heroImage.media.url}
                alt={project.name}
                fill
                className="object-cover"
                priority
                sizes="100vw"
              />
            )
          )}

          {/* Gradient Overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${colors.secondary}00 0%, ${colors.secondary}99 100%)`,
            }}
          />
        </motion.div>

        {/* Interactive Hotspots */}
        {hotspots.map((hotspot) => (
          <motion.div
            key={hotspot.id}
            className="absolute z-10 cursor-pointer"
            style={{
              left: `${hotspot.x}%`,
              top: `${hotspot.y}%`,
            }}
            initial={{ scale: 0 }}
            animate={isHeroInView ? { scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
            onHoverStart={() => setActiveHotspot(hotspot.id)}
            onHoverEnd={() => setActiveHotspot(null)}
          >
            {/* Pulsing Pin */}
            <div className="relative">
              <motion.div
                className="h-4 w-4 rounded-full"
                style={{ backgroundColor: colors.accent }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [1, 0.5, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              
              {/* Tooltip */}
              {activeHotspot === hotspot.id && (
                <motion.div
                  className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-white p-3 shadow-2xl"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-sm font-bold text-gray-900">
                    {hotspot.title}
                  </div>
                  <div className="text-xs text-gray-600">
                    {hotspot.description}
                  </div>
                  <div
                    className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1 rotate-45 bg-white"
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Floating Brand Logo */}
        {brandLogo && (
          <motion.div
            className="absolute left-8 top-8 z-20 lg:left-16 lg:top-16"
            style={{ opacity }}
          >
            <div className="rounded-2xl bg-white/95 p-6 shadow-2xl backdrop-blur-sm">
              <Image
                src={brandLogo}
                alt={`${project.name} logo`}
                width={180}
                height={60}
                className="h-auto w-32 object-contain lg:w-44"
              />
            </div>
          </motion.div>
        )}

        {/* Hero Content */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 z-10 px-8 pb-16 lg:px-16 lg:pb-20"
          style={{ opacity }}
        >
          <div className="mx-auto max-w-7xl">
            {/* Project Type Badge */}
            {project.projectType && (
              <motion.div
                className="mb-4 inline-block"
                initial={{ opacity: 0, x: -20 }}
                animate={isHeroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span
                  className="inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white"
                  style={{ backgroundColor: colors.accent }}
                >
                  {project.projectType.replace(/_/g, ' ')}
                </span>
              </motion.div>
            )}

            {/* Project Name */}
            <motion.h1
              className="mb-6 text-5xl font-bold leading-tight text-white lg:text-8xl"
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {project.name}
            </motion.h1>

            {/* Description */}
            <motion.p
              className="mb-8 max-w-3xl text-lg leading-relaxed text-white/90 lg:text-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {project.description}
            </motion.p>

            {/* Key Stats */}
            <motion.div
              className="flex flex-wrap gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {project.totalUnits && (
                <div className="rounded-lg bg-white/10 px-6 py-4 backdrop-blur-md">
                  <div className="text-3xl font-bold" style={{ color: colors.accent }}>
                    {project.totalUnits}
                  </div>
                  <div className="text-sm font-medium text-white/80">Total Units</div>
                </div>
              )}

              {project.displayPriceText && (
                <div className="rounded-lg bg-white/10 px-6 py-4 backdrop-blur-md">
                  <div className="text-3xl font-bold" style={{ color: colors.accent }}>
                    {project.displayPriceText}
                  </div>
                  <div className="text-sm font-medium text-white/80">Starting From</div>
                </div>
              )}

              {project.estimatedCompletionDate && (
                <div className="rounded-lg bg-white/10 px-6 py-4 backdrop-blur-md">
                  <div className="text-3xl font-bold" style={{ color: colors.accent }}>
                    {new Date(project.estimatedCompletionDate).getFullYear()}
                  </div>
                  <div className="text-sm font-medium text-white/80">Completion</div>
                </div>
              )}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <motion.button
                className="group flex items-center gap-3 rounded-full px-8 py-4 text-lg font-semibold text-white shadow-2xl"
                style={{ backgroundColor: colors.primary }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {messages.exploreProject}
                <motion.svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </motion.svg>
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={isHeroInView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wider text-white/80">
              {messages.scrollToDiscover}
            </span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <svg
                className="h-6 w-6 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Additional Content Sections with Parallax Layers
          (Overview, Features, Location sections would go here) */}
    </div>
  );
}

